import type {
  IAvailability,
  IIntervals,
  IParsedAvailabilityData,
  IParsedItem,
} from '@/components/agent/my-availability/types';
import type { ScheduleId } from '@/types/schedule';

import { getOffsetData, initialParsedItem } from '../contracts';
import { splitAvailabilityIntervals } from './splitAvailabilityIntervals';
import { sortBy } from '@/helpers';

const createParsedData = (
  data: IAvailability[],
  lastAvailabilityList: IAvailability[],
  lastParsedData: IParsedAvailabilityData[],
  selectedSchedulesIds: ScheduleId[],
  timeZone: string,
): IParsedAvailabilityData[] =>
  data
    .filter(item => selectedSchedulesIds.includes(item.scheduleId))
    .map(item => {
      const { intervals: lastIntervals } = lastAvailabilityList.find(d => d.scheduleId === item.scheduleId) || {};
      const { parsedData: lastParsedDataObj } = lastParsedData.find(d => d.scheduleId === item.scheduleId) || {};

      const newIntervals: IIntervals[] = splitAvailabilityIntervals(item.intervals, timeZone);

      const parsedItems: IParsedItem[] = newIntervals.flatMap((interval, intervalIndex) => {
        const isAlreadyAdded = !lastIntervals?.includes(interval);
        if (!isAlreadyAdded) return [];

        const offsetData = getOffsetData(interval.startOffset, interval.endOffset, timeZone);

        return {
          ...initialParsedItem,
          ...lastParsedDataObj?.[intervalIndex],
          ...offsetData,
          colSpan: interval.colSpan,
          colOffset: interval.colOffset,
        };
      });

      const sortedParsedItems = sortBy(parsedItems, 'startHour.weekdayIndex', 'minutesFromMidnight', 'blockDuration');

      return {
        ...item,
        intervals: newIntervals,
        parsedData: sortedParsedItems,
      };
    });

const mergeIntervals = (data: IParsedAvailabilityData[]): IParsedAvailabilityData[] => {
  data.forEach((item, index) => {
    item.intervals
      .filter(interval => interval.isNew !== true)
      .forEach((interval, intervalIndex) => {
        let mergeSize = 1;
        const nextSchedules = data.filter((_, id) => id > index);

        for (let x = 0, len = nextSchedules.length; x < len; x++) {
          const hasSameInterval = nextSchedules[x].intervals.some(
            nextInterval =>
              nextInterval.isNew !== true &&
              nextInterval.startOffset === interval.startOffset &&
              nextInterval.endOffset === interval.endOffset,
          );
          if (!hasSameInterval) break;
          mergeSize = mergeSize + 1;
        }

        if (mergeSize > 1) {
          for (let x = index; x <= mergeSize; x++) {
            const nextSchedule = data[x + 1];
            if (!nextSchedule) break;

            const intervalToRemove = nextSchedule.intervals.findIndex(
              obj =>
                obj.isNew !== true && obj.startOffset === interval.startOffset && obj.endOffset === interval.endOffset,
            );

            if (intervalToRemove === -1) break;
            nextSchedule.intervals = nextSchedule.intervals.filter((_, id) => id !== intervalToRemove);
            nextSchedule.parsedData = nextSchedule.parsedData.filter((_, id) => id !== intervalToRemove);
          }

          item.intervals = item.intervals.map((obj, id) => ({
            ...obj,
            ...(id === intervalIndex && { colSpan: mergeSize }),
          }));
          item.parsedData = item.parsedData.map((obj, id) => ({
            ...obj,
            ...(id === intervalIndex && { colSpan: mergeSize }),
          }));
        }
      });
  });

  return data;
};

const parseAvailabilityData = (
  availabilityList: IAvailability[],
  lastAvailabilityList: IAvailability[],
  lastParsedData: IParsedAvailabilityData[],
  selectedSchedulesIds: ScheduleId[],
  timeZone: string,
): IParsedAvailabilityData[] => {
  const parsedData = createParsedData(
    availabilityList,
    lastAvailabilityList,
    lastParsedData,
    selectedSchedulesIds,
    timeZone,
  );

  return mergeIntervals(parsedData);
};

export { createParsedData, mergeIntervals, parseAvailabilityData };
